import React, { useState } from 'react';
import { useGame } from '../context/GameContext';
import { getAllGameTypes } from '../utils/gameTypes';
import './HomePage.css';

/**
 * Home page - Select game type, Create or Join a game
 */
const HomePage = () => {
  const { createNewGame, joinExistingGame, loading, error } = useGame();
  const [selectedGameType, setSelectedGameType] = useState(null);
  const [mode, setMode] = useState(null); // 'create' or 'join'
  const [playerName, setPlayerName] = useState('');
  const [gameId, setGameId] = useState('');

  const gameTypes = getAllGameTypes();

  // Check for join code from QR scan (URL or sessionStorage)
  React.useEffect(() => {
    // First check URL directly
    const params = new URLSearchParams(window.location.search);
    const joinCode = params.get('join');
    
    if (joinCode) {
      setGameId(joinCode.toUpperCase());
      setMode('join');
      // Clear the URL parameter
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    
    // Then check sessionStorage (set by App.js)
    const pendingCode = sessionStorage.getItem('pendingJoinCode');
    if (pendingCode) {
      setGameId(pendingCode.toUpperCase());
      setMode('join');
      sessionStorage.removeItem('pendingJoinCode');
    }
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!playerName.trim() || !selectedGameType) return;
    await createNewGame(playerName.trim(), selectedGameType.id);
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!playerName.trim() || !gameId.trim()) return;
    await joinExistingGame(gameId.trim(), playerName.trim());
  };

  // Game Type Selection
  if (!selectedGameType && !mode) {
    return (
      <div className="home-page">
        <div className="home-container fade-in">
          {/* Logo */}
          <div className="logo-section">
            <span className="logo-emoji">🃏</span>
            <h1 className="logo-title">Card Games</h1>
            <p className="logo-subtitle">Choose your game</p>
          </div>

          {/* Game Type Selection */}
          <div className="game-type-selection">
            {gameTypes.map((gameType) => (
              <button
                key={gameType.id}
                className="game-type-btn"
                onClick={() => setSelectedGameType(gameType)}
              >
                <span className="game-type-emoji">{gameType.emoji}</span>
                <span className="game-type-name">{gameType.name}</span>
                <span className="game-type-desc">{gameType.description}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page">
      <div className="home-container fade-in">
        {/* Logo */}
        <div className="logo-section">
          <span className="logo-emoji">{selectedGameType?.emoji || '🎱'}</span>
          <h1 className="logo-title">{selectedGameType?.name || 'Card Game'}</h1>
          <p className="logo-subtitle">{selectedGameType?.description || 'Digital card game'}</p>
        </div>

        {/* Mode Selection */}
        {selectedGameType && !mode && (
          <div className="mode-selection">
            <button 
              className="mode-btn create" 
              onClick={() => setMode('create')}
            >
              <span className="mode-icon">🎮</span>
              <span className="mode-text">Create Game</span>
              <span className="mode-desc">Host a new game session</span>
            </button>
            <button 
              className="mode-btn join" 
              onClick={() => setMode('join')}
            >
              <span className="mode-icon">🔗</span>
              <span className="mode-text">Join Game</span>
              <span className="mode-desc">Enter a game code</span>
            </button>
            <button
              className="back-btn"
              onClick={() => setSelectedGameType(null)}
              style={{ marginTop: '16px', textAlign: 'center', width: '100%' }}
            >
              ← Back to Game Selection
            </button>
          </div>
        )}

        {/* Create Game Form */}
        {mode === 'create' && selectedGameType && (
          <form className="game-form" onSubmit={handleCreate}>
            <button 
              type="button" 
              className="back-btn"
              onClick={() => setMode(null)}
            >
              ← Back
            </button>
            <h2>Create New Game</h2>
            <div className="input-group">
              <label>Your Name</label>
              <input
                type="text"
                placeholder="Enter your name"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                maxLength={20}
                autoFocus
              />
            </div>
            <button 
              type="submit" 
              className="submit-btn"
              disabled={!playerName.trim() || loading}
            >
              {loading ? 'Creating...' : `${selectedGameType.emoji} Create Game`}
            </button>
          </form>
        )}

        {/* Join Game Form */}
        {mode === 'join' && (
          <form className="game-form" onSubmit={handleJoin}>
            <button 
              type="button" 
              className="back-btn"
              onClick={() => setMode(null)}
            >
              ← Back
            </button>
            <h2>Join Game</h2>
            <div className="input-group">
              <label>Your Name</label>
              <input
                type="text"
                placeholder="Enter your name"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                maxLength={20}
                autoFocus
              />
            </div>
            <div className="input-group">
              <label>Game Code</label>
              <input
                type="text"
                placeholder="Enter 6-character code"
                value={gameId}
                onChange={(e) => setGameId(e.target.value.toUpperCase())}
                maxLength={6}
                className="game-code-input"
              />
            </div>
            <button 
              type="submit" 
              className="submit-btn"
              disabled={!playerName.trim() || !gameId.trim() || loading}
            >
              {loading ? 'Joining...' : '🔗 Join Game'}
            </button>
          </form>
        )}

        {/* Error Message */}
        {error && (
          <div className="error-message">
            ⚠️ {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;

